import { useLandingLanguage } from '../../components/Public/LandingLanguage';
import React, { useEffect, useRef } from 'react';
import { ArrowDownward, CalendarTodayOutlined, Check, ChairOutlined, PeopleOutline, Wifi } from '@mui/icons-material';
import './PhoneStory.css';

const ringLength = 2 * Math.PI * 44;

const PhoneStory = ({ day }: { day: Date }) => {
  const { t, locale } = useLandingLanguage();
  const root = useRef<HTMLElement>(null);
  const scene = useRef<HTMLDivElement>(null);
  const transit = useRef<HTMLDivElement>(null);
  const phone = useRef<HTMLDivElement>(null);
  const counter = useRef<HTMLSpanElement>(null);
  const date = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', timeZone: 'Europe/Bucharest' }).format(day);

  useEffect(() => {
    if (!window.matchMedia || typeof IntersectionObserver === 'undefined') return;
    let active = true;
    let dispose: (() => void) | undefined;
    // Keep complete static content on the server and if the animation chunk fails.
    Promise.all([import('gsap'), import('gsap/ScrollTrigger')]).then(([{ gsap }, { ScrollTrigger }]) => {
      if (!active || !root.current || !scene.current || !transit.current || !phone.current) return;
      gsap.registerPlugin(ScrollTrigger);
      const media = gsap.matchMedia();
      dispose = () => media.revert();
      media.add({ desktop: '(min-width: 1000px) and (min-height: 720px)', motion: '(prefers-reduced-motion: no-preference)' }, context => {
        if (!context.conditions?.motion) return;
        const desktop = Boolean(context.conditions.desktop);
        const widgets = root.current!.querySelectorAll('.orarivo-cinematic-widget');
        const badges = root.current!.querySelectorAll('.orarivo-cinematic-badge');
        const ring = root.current!.querySelector('.orarivo-cinematic-progress-value');
        const count = { value: 0 };
        const timeline = gsap.timeline({ scrollTrigger: {
          trigger: desktop ? root.current! : root.current!.querySelector('.orarivo-cinematic-phone-stage') || root.current!,
          start: desktop ? 'top top+=81' : 'top 84%',
          end: desktop ? () => `+=${Math.min(window.innerHeight * 1.4, 1400)}` : 'top 14%',
          pin: desktop ? scene.current : false,
          scrub: desktop ? .7 : .35,
          anticipatePin: 1,
          invalidateOnRefresh: true,
        } });
        timeline.fromTo(transit.current,
          { y: desktop ? 150 : 80, z: desktop ? -280 : 0, rotationX: desktop ? 36 : 20, rotationY: desktop ? -28 : -14, rotationZ: -5, scale: desktop ? .7 : .84 },
          { y: 0, z: 0, rotationX: 0, rotationY: 0, rotationZ: 0, scale: 1, duration: desktop ? 2.2 : 2.8, ease: desktop ? 'power3.out' : 'power1.inOut' }, 0)
          .fromTo(widgets, { y: 24, opacity: .3 }, { y: 0, opacity: 1, stagger: .1, duration: desktop ? .8 : 1, ease: 'power3.out' }, desktop ? .6 : .7)
          .fromTo(ring, { strokeDashoffset: ringLength }, { strokeDashoffset: ringLength * (1 - 8 / 15), duration: desktop ? 1.2 : 1.7, ease: desktop ? 'power2.out' : 'none' }, desktop ? .8 : 1.1)
          .fromTo(count, { value: 0 }, { value: 8, duration: desktop ? 1.2 : 1.7, ease: desktop ? 'power2.out' : 'none', onUpdate: () => { if (counter.current) counter.current.textContent = String(Math.round(count.value)); } }, desktop ? .8 : 1.1)
          .fromTo(badges, { y: 50, opacity: 0, scale: .85 }, { y: 0, opacity: 1, scale: 1, stagger: .15, duration: desktop ? .9 : 1.1, ease: 'power3.out' }, desktop ? 1 : 1.3)
          .to({}, { duration: desktop ? .6 : .35 });
        if (desktop) timeline.to(transit.current, { y: -20, rotationY: 6, scale: .96, duration: .7, ease: 'power2.inOut' });
        timeline.scrollTrigger?.refresh();
        timeline.progress(timeline.scrollTrigger?.progress ?? 0);
        const turnX = gsap.quickTo(phone.current, 'rotationX', { duration: .7, ease: 'power3.out' });
        const turnY = gsap.quickTo(phone.current, 'rotationY', { duration: .7, ease: 'power3.out' });
        const onMove = (event: PointerEvent) => {
          if (!desktop || event.pointerType !== 'mouse') return;
          const bounds = scene.current!.getBoundingClientRect();
          turnY(((event.clientX - bounds.left) / bounds.width - .5) * 14);
          turnX(-((event.clientY - bounds.top) / bounds.height - .5) * 10);
        };
        const onLeave = () => { turnX(0); turnY(0); };
        const target = scene.current!;
        target.addEventListener('pointermove', onMove);
        target.addEventListener('pointerleave', onLeave);
        return () => {
          target.removeEventListener('pointermove', onMove);
          target.removeEventListener('pointerleave', onLeave);
          if (counter.current) counter.current.textContent = '8';
        };
      }, root.current);
      let lastWidth = root.current.getBoundingClientRect().width;
      const resize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(([entry]) => {
        if (Math.abs(entry.contentRect.width - lastWidth) > 1) {
          lastWidth = entry.contentRect.width;
          ScrollTrigger.refresh();
        }
      });
      resize?.observe(root.current);
      dispose = () => { resize?.disconnect(); media.revert(); };
      ScrollTrigger.refresh();
    }).catch(() => {});
    return () => { active = false; dispose?.(); };
  }, [locale]);

  return <section className="orarivo-cinematic" ref={root} id="in-buzunar" aria-labelledby="phone-story-title">
    <div className="orarivo-cinematic-scene" ref={scene}>
      <div className="orarivo-cinematic-layout">
        <div className="orarivo-cinematic-copy"><h2 id="phone-story-title">{t("Tot ce urmează.")}<br/>{t("La îndemână.")}</h2><p>{t("Programările de azi, echipa, clienții și detaliile rezervărilor. Deschizi Orarivo în browser și ai agenda cu tine.")}</p><a href="#cum-functioneaza">{t("Continuă prezentarea")} <ArrowDownward fontSize="small"/></a></div>
        <div className="orarivo-cinematic-phone-stage">
          <div className="orarivo-cinematic-transit" ref={transit}>
            <div className="orarivo-cinematic-phone" ref={phone} role="img" aria-label={t("Exemplu fictiv de frizerie pe telefon: Maria are o programare la 10:00 pentru Andrei, la scaunul 2, și 8 din 15 programări încheiate.")}>
              <span className="orarivo-cinematic-hardware hardware-volume" aria-hidden="true"/><span className="orarivo-cinematic-hardware hardware-power" aria-hidden="true"/>
              <div className="orarivo-cinematic-screen" aria-hidden="true">
                <div className="orarivo-cinematic-status"><span>9:41</span><span className="orarivo-cinematic-island"/><Wifi fontSize="small"/></div>
                <div className="orarivo-cinematic-appbar"><img src={`${process.env.PUBLIC_URL}/favicon.svg`} alt=""/><strong>{t("Programările mele")}</strong><span>M</span></div>
                <div className="orarivo-cinematic-screen-content">
                  <div className="orarivo-cinematic-widget cinematic-greeting"><strong>{t("Bună, Maria.")}</strong><span>{date} · {t("Exemplu ilustrativ")}</span></div>
                  <div className="orarivo-cinematic-widget orarivo-cinematic-progress"><svg viewBox="0 0 112 112"><circle cx="56" cy="56" r="44" fill="none" stroke="#e3edf9" strokeWidth="7"/><circle className="orarivo-cinematic-progress-value" cx="56" cy="56" r="44" fill="none" stroke="#185eac" strokeWidth="7" strokeLinecap="round" strokeDasharray={ringLength} strokeDashoffset={ringLength * (1 - 8 / 15)}/></svg><div><strong><span ref={counter}>8</span><span> / 15</span></strong><span>{t("programări")}</span></div></div>
                  <div className="orarivo-cinematic-widget orarivo-cinematic-appointment"><span><CalendarTodayOutlined fontSize="small"/>{t("Astăzi")}</span><strong>10:00 <span>– 10:45</span></strong><h3>{t("Tuns și barbă")}</h3><span><PeopleOutline fontSize="small"/>{t("Andrei · Client")}</span><span><ChairOutlined fontSize="small"/>{t("Scaun 2")}</span><span className="cinematic-confirmed"><Check fontSize="small"/>{t("Confirmată")}</span></div>
                  <div className="orarivo-cinematic-widget orarivo-cinematic-history"><div><span>{t("Frizerie")}</span><strong>{t("Agenda de azi")}</strong></div><Check fontSize="small"/></div>
                </div>
                <span className="orarivo-cinematic-home-bar"/>
                <div className="orarivo-cinematic-glare"/>
              </div>
            </div>
          </div>
          <div className="orarivo-cinematic-badge badge-appointment" aria-hidden="true"><CalendarTodayOutlined/><div><strong>{t("Azi, la 10:00")}</strong><span>{t("Programarea ta e confirmată.")}</span></div></div>
          <div className="orarivo-cinematic-badge badge-progress" aria-hidden="true"><Check/><div><strong>{t("Fiecare client, la timp.")}</strong><span>{t("Programările, la un loc.")}</span></div></div>
        </div>
        <div className="orarivo-cinematic-brand"><div><img src={`${process.env.PUBLIC_URL}/favicon.svg`} alt=""/><span>orarivo</span></div><p>{t("Același cont.")}<br/>{' '}{t("Oriunde ești.")}</p><span><Check fontSize="small"/>{t("Fără instalare.")}</span></div>
      </div>
      <p className="orarivo-cinematic-note">{t("Previzualizare ilustrativă · date fictive.")}</p>
    </div>
  </section>;
};
export default PhoneStory;
